import { useEffect, useRef, useState } from 'react';
import { getPetPhotoSignedUrl } from '../services/storageService';

/**
 * Hook que dado un pet, obtiene y mantiene fresca la signed URL de su foto
 * desde el worker. Devuelve `{ url, isLoading, error }`.
 *
 * - Si `pet.photoExt` es null/undefined: devuelve `{ url: null, isLoading: false, error: null }`
 *   sin hacer ninguna request.
 * - Si tiene `photoExt`: pide signed URL al worker, cachea el resultado en
 *   un Map de módulo (compartido entre componentes), y refresca antes de que
 *   expire el TTL (~10 min).
 *
 * Cache:
 *   Compartida a nivel módulo para que si una pet aparece en varios
 *   componentes simultáneamente (ej. PetCard en la lista + Avatar en TopBar)
 *   solo se haga 1 request. La cache es por sesión del browser: se pierde
 *   al recargar la página. No hace falta más persistencia porque las URLs
 *   firmadas son cortas y la app no se preocupa por sobrevivir reloads.
 *
 * Invalidación:
 *   Después de upload/delete de foto, llamar `invalidatePetPhotoCache(petId)`
 *   desde PetsContext para forzar refetch en los componentes montados.
 */

const CACHE = new Map(); // key: `${petId}:${ext}`, value: { url, expiresAt }
const REFRESH_BUFFER_MS = 60 * 1000; // refrescar 1 min antes de expirar

/**
 * Suscribers a la invalidación del cache. Cada hook agrega su setter al map
 * por petId. Cuando llamamos invalidatePetPhotoCache(petId), notificamos a
 * todos los hooks suscritos para que vuelvan a fetchear.
 */
const SUBSCRIBERS = new Map(); // key: petId, value: Set<() => void>

const subscribe = (petId, onInvalidate) => {
  if (!SUBSCRIBERS.has(petId)) SUBSCRIBERS.set(petId, new Set());
  SUBSCRIBERS.get(petId).add(onInvalidate);
  return () => {
    const set = SUBSCRIBERS.get(petId);
    if (!set) return;
    set.delete(onInvalidate);
    if (set.size === 0) SUBSCRIBERS.delete(petId);
  };
};

export const invalidatePetPhotoCache = (petId) => {
  for (const key of Array.from(CACHE.keys())) {
    if (key.startsWith(`${petId}:`)) CACHE.delete(key);
  }
  const subs = SUBSCRIBERS.get(petId);
  if (subs) subs.forEach((fn) => fn());
};

const cacheKeyFor = (petId, ext) => `${petId}:${ext}`;

export const usePetPhoto = (pet) => {
  const petId = pet?.id ?? null;
  const photoExt = pet?.photoExt ?? null;

  const [state, setState] = useState({
    url: null,
    isLoading: false,
    error: null,
  });

  // Tick interno para forzar refetch desde la invalidación externa.
  const [tick, setTick] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Suscripción a invalidaciones para este petId.
  useEffect(() => {
    if (!petId) return undefined;
    return subscribe(petId, () => {
      if (mountedRef.current) setTick((t) => t + 1);
    });
  }, [petId]);

  useEffect(() => {
    if (!petId || !photoExt) {
      setState({ url: null, isLoading: false, error: null });
      return undefined;
    }

    const key = cacheKeyFor(petId, photoExt);
    const now = Date.now();
    const cached = CACHE.get(key);

    if (cached && cached.expiresAt - now > REFRESH_BUFFER_MS) {
      setState({ url: cached.url, isLoading: false, error: null });
      return undefined;
    }

    let cancelled = false;
    setState((s) => ({ ...s, isLoading: true }));

    getPetPhotoSignedUrl({ petId, ext: photoExt })
      .then((result) => {
        if (cancelled || !mountedRef.current) return;
        if (!result) {
          // El doc dice que hay foto pero Supabase no la tiene (caso edge:
          // se borró desde la consola, o falló a mitad del upload). El
          // componente cae al fallback (iniciales / placeholder).
          setState({ url: null, isLoading: false, error: null });
          return;
        }
        const ttlMs = (result.expiresInSeconds ?? 600) * 1000;
        CACHE.set(key, {
          url: result.signedUrl,
          expiresAt: Date.now() + ttlMs,
        });
        setState({ url: result.signedUrl, isLoading: false, error: null });
      })
      .catch((err) => {
        if (cancelled || !mountedRef.current) return;
        setState({ url: null, isLoading: false, error: err });
      });

    return () => {
      cancelled = true;
    };
  }, [petId, photoExt, tick]);

  return state;
};
